import React, { useEffect, useState, useRef } from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { ClockIcon, MapPinIcon, Trash2Icon, SettingsIcon, CheckIcon, XIcon, UsersIcon } from 'lucide-react';
import { Session, Meeting } from '../types';
interface ScheduleItem {
  id: string;
  type: 'session' | 'meeting';
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  status?: 'booked' | 'confirmed' | 'pending' | 'declined';
  data: Session | Meeting;
  featureName?: string;
  featureColor?: {
    bg: string;
    text: string;
  };
}
interface SwipeableScheduleCardProps {
  item: ScheduleItem;
  primaryColor: string;
  onRemoveSession: (sessionId: string) => void;
  onManageMeeting: (meetingId: string) => void;
  onAcceptMeeting: (meetingId: string) => void;
  onDeclineMeeting: (meetingId: string) => void;
  hasConflict?: boolean;
}
export function SwipeableScheduleCard({
  item,
  primaryColor,
  onRemoveSession,
  onManageMeeting,
  onAcceptMeeting,
  onDeclineMeeting,
  hasConflict = false
}: SwipeableScheduleCardProps) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const maxSwipe = item.type === 'session' ? -100 : -120;
  const swipeThreshold = 50;
  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      const diff = touch.clientX - startX;
      const newOffset = Math.max(maxSwipe, Math.min(0, diff));
      setSwipeOffset(newOffset);
    };
    const handleTouchEnd = () => {
      if (!isDragging) return;
      setIsDragging(false);
      if (swipeOffset < -swipeThreshold) {
        setSwipeOffset(maxSwipe);
      } else {
        setSwipeOffset(0);
      }
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const diff = e.clientX - startX;
      const newOffset = Math.max(maxSwipe, Math.min(0, diff));
      setSwipeOffset(newOffset);
    };
    const handleMouseUp = () => {
      if (!isDragging) return;
      setIsDragging(false);
      if (swipeOffset < -swipeThreshold) {
        setSwipeOffset(maxSwipe);
      } else {
        setSwipeOffset(0);
      }
    };
    if (isDragging) {
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, startX, swipeOffset, maxSwipe]);
  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };
  const handleAction = (action: () => void) => {
    setSwipeOffset(0);
    setTimeout(action, 150);
  };
  // Get meeting status label (matching Networking component)
  const getMeetingStatusLabel = (meeting: Meeting) => {
    if (meeting.status === 'confirmed') return 'Confirmed';
    if (meeting.requestedBy === 'me') return 'Awaiting Response';
    return 'Respond to Invite';
  };
  // Get meeting status badge variant
  const getMeetingStatusVariant = (meeting: Meeting): 'success' | 'warning' | 'default' => {
    if (meeting.status === 'confirmed') return 'success';
    if (meeting.requestedBy === 'me') return 'warning';
    return 'default';
  };
  const getStatusBadge = () => {
    if (item.type === 'meeting') {
      const meeting = item.data as Meeting;
      return <Badge variant={getMeetingStatusVariant(meeting)} size="sm" data-id="element-4187">
          {getMeetingStatusLabel(meeting)}
        </Badge>;
    }
    return null;
  };
  const isPendingInvite = item.type === 'meeting' && item.status === 'pending' && (item.data as Meeting).requestedBy === 'them';
  return <div ref={cardRef} className={`relative overflow-hidden rounded-lg ${hasConflict ? 'ring-2 ring-red-500' : ''}`} style={{
    touchAction: 'pan-y'
  }} data-id="element-4188">
      {/* Action Buttons Background */}
      <div className="absolute inset-y-0 right-0 flex items-stretch" data-id="element-4189">
        {item.type === 'session' && <button onClick={() => onRemoveSession?.(item.id)} className="w-24 flex items-center justify-center bg-red-500 text-white font-gotham font-medium text-sm" data-id="element-4190">
            <XIcon className="w-5 h-5 mr-1" data-id="element-4191" />
            Remove
          </button>}

        {item.type === 'meeting' && <button onClick={() => onManageMeeting?.(item.id)} className="w-28 flex items-center justify-center bg-blue-500 text-white font-gotham font-medium text-sm" data-id="element-4192">
            <SettingsIcon className="w-5 h-5 mr-1" data-id="element-4193" />
            Manage
          </button>}
      </div>

      {/* Swipeable Card */}
      <div onTouchStart={handleTouchStart} onMouseDown={handleMouseDown} style={{
      transform: `translateX(${swipeOffset}px)`,
      transition: isDragging ? 'none' : 'transform 0.3s ease-out'
    }} className="relative bg-white cursor-grab active:cursor-grabbing" data-id="element-4194">
        <Card padding="md" data-id="element-4195">
          <div className="flex items-start gap-3" data-id="element-4196">
            <div className="flex-shrink-0 w-12 text-center py-2 rounded-lg" style={{
            backgroundColor: `${primaryColor}10`
          }} data-id="element-4197">
              <div className="text-lg font-bold font-gotham" style={{
              color: primaryColor
            }} data-id="element-4198">
                {item.startTime.split(':')[0]}
              </div>
              <div className="text-xs text-gray-500 font-gotham" data-id="element-4199">
                {item.startTime.split(':')[1]}
              </div>
            </div>

            <div className="flex-1 min-w-0" data-id="element-4200">
              <div className="flex items-start justify-between gap-2 mb-2" data-id="element-4201">
                {/* Feature Badge with Color for Sessions */}
                {item.type === 'session' && item.featureName && item.featureColor ? <div className="px-2 py-1 rounded-md text-xs font-semibold font-gotham" style={{
                backgroundColor: item.featureColor.bg,
                color: item.featureColor.text
              }} data-id="element-4202">
                    {item.featureName}
                  </div> : <Badge variant={item.type === 'session' ? 'default' : 'warning'} size="sm" data-id="element-4203">
                    {item.type === 'session' ? 'Session' : 'Meeting'}
                  </Badge>}
                {getStatusBadge()}
              </div>

              <h4 className="font-bold text-gray-900 font-gotham mb-1" data-id="element-4204">
                {item.title}
              </h4>

              {item.type === 'meeting' && <p className="text-sm text-gray-600 font-gotham mb-2" data-id="element-4205">
                  {(item.data as Meeting).attendeeRole}{' '}
                  {(item.data as Meeting).attendeeCompany && `at ${(item.data as Meeting).attendeeCompany}`}
                </p>}

              <div className="flex items-center gap-3 text-sm text-gray-600 font-gotham mb-2" data-id="element-4206">
                <span className="flex items-center gap-1" data-id="element-4207">
                  <ClockIcon className="w-4 h-4" data-id="element-4208" />
                  {item.startTime} - {item.endTime}
                </span>
                <span className="flex items-center gap-1" data-id="element-4209">
                  <MapPinIcon className="w-4 h-4" data-id="element-4210" />
                  {item.location}
                </span>
              </div>

              {item.type === 'session' && (item.data as Session).capacity && <div className="flex items-center gap-1 text-xs text-gray-500 font-gotham mb-2" data-id="element-4211">
                  <UsersIcon className="w-3 h-3" data-id="element-4212" />
                  {(item.data as Session).currentAttendees}/
                  {(item.data as Session).capacity} attending
                </div>}

              {/* Pending Invite Actions - Below timings/location */}
              {isPendingInvite && <div className="mt-3 flex gap-2" data-id="element-4213">
                  <button onClick={e => {
                e.stopPropagation();
                onAcceptMeeting?.(item.id);
              }} className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium font-gotham text-white transition-all hover:shadow-md" style={{
                backgroundColor: primaryColor
              }} data-id="element-4214">
                    <CheckIcon className="w-4 h-4" data-id="element-4215" />
                    Accept
                  </button>
                  <button onClick={e => {
                e.stopPropagation();
                onDeclineMeeting?.(item.id);
              }} className="flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 text-sm font-medium font-gotham transition-all hover:bg-opacity-10" style={{
                borderColor: primaryColor,
                color: primaryColor
              }} data-id="element-4216">
                    <XIcon className="w-4 h-4" data-id="element-4217" />
                    Decline
                  </button>
                </div>}
            </div>
          </div>

          {/* Swipe Hint and Conflict Tag */}
          <div className="mt-2 flex items-center justify-between" data-id="element-4218">
            <div className="inline-flex items-center gap-1 text-xs text-gray-400 font-gotham" data-id="element-4219">
              <span data-id="element-4220">← Swipe for actions</span>
            </div>
            {hasConflict && <div className="bg-red-50 border-2 border-red-500 text-red-900 px-2 py-1 rounded-md text-xs font-bold font-gotham" data-id="element-4221">
                Conflict
              </div>}
          </div>
        </Card>
      </div>
    </div>;
}